import {Injectable} from "@angular/core";
import {Constants} from "./utils/Constants";
import apiConfig from "./api-client/hav-api-config";
import {HavApiRequestService} from "./api-client/hav-api-.service";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "./utils/state-management/store";
import {
  EventAutoGdoStatus,
  EventGdoStatus, GdoBatteryStatus,
  GdoEventBy,
  GdoEventGroup,
  GdoVacationMode,
  GdoWifiStatus,
  IGDOHistory
} from "../secure/models/hav-models";
import {getMonthAndDay, getTimeFromTimestamp} from "./utils/utility";
import {Config} from "./utils/gdo-config";

@Injectable()
export class GDOService {

  constructor(
    public apiService: HavApiRequestService,
    private ngRedux: NgRedux<IAppState>
  ) {
  }

  getGdoDetails(gdoId: string) {
    let pathParams = {
      id: gdoId,
    };
    return this.apiService
      .executeHttp(
        Constants.API_METHODS.GET, apiConfig.getGdoDetails.uri,
        null,
        false,
        pathParams
      );
  }

  filterGDOEvent(gdoId: string, body: any) {
    let pathParams = {
      id: gdoId,
    };
    let url = apiConfig.getGdoHistory.uri;
    return this.apiService
      .executeHttp(
        Constants.API_METHODS.POST, url,
        body,
        true,
        pathParams
      );
  }

  filterHomeEvent(homeId: string, body: any) {
    let pathParams = {
      id: homeId,
    };
    let url = apiConfig.filterHomeEventHistory.uri;
    return this.apiService
      .executeHttp(
        Constants.API_METHODS.POST, url,
        body,
        true,
        pathParams
      );
  }

  getDeviceName(gdoId: string): any {
    const devices = this.ngRedux.getState().devices;
    let response = {
      deviceName: '',
      homeId: ''
    }
    Object.values(devices).forEach(deviceList => {
      const deviceArray = Array.isArray(deviceList) ? deviceList : Object.values(deviceList);
      const device = deviceArray.find(device => device.deviceId === gdoId && device.hasOwnProperty('manualMatterCode'));
      if (device) {
        response.deviceName = device.deviceName;
        response.homeId = device.homeId;
      }
    });
    return response
  }

  getHomeName(homeId: string) {
    const homes = this.ngRedux.getState().homes;
    const home = homes.find(h => h.homeId === homeId);
    return home.homeName;
  }

  mappingEvent(event: IGDOHistory, langCode: string, fromHomePage: boolean) {
    event.date = getMonthAndDay(event.timeStamp);
    event.time = getTimeFromTimestamp(event.timeStamp);
    this.getEventBy(event, langCode);

    const setEvent = (name: string, icon: string, color: string, des: string, percentage?: number) => {
      event.eventName = this.setEventName(langCode, name, fromHomePage, event, percentage);
      event.eventIcon = icon;
      event.eventColor = color;
      event.eventDes = des;
    };

    switch (event.eventGroupId) {
      case GdoEventGroup.GDO_OPERATION:
      case GdoEventGroup.AUTO_OPERATION:
        this.handleGdoOperationEvent(event, langCode, setEvent);
        break;
      case GdoEventGroup.WIFI_MANAGEMENT:
        this.handleWifiManagementEvent(event, langCode, setEvent);
        break;
      case GdoEventGroup.VACATION_MODE:
        this.handleVacationModeEvent(event, langCode, setEvent);
        break;
      case GdoEventGroup.BATTERY:
        this.handleBatteryEvent(event, langCode, setEvent);
        break;
      case GdoEventGroup.GDO_MANAGEMENT:
        setEvent(Config.EVENT.EVENT_NAME.DEVICE_ACTIVATION[langCode], "../../../assets/images/gdo/event/Check_List.svg", Config.EVENT.COLOR.Blue, event.eventDes);
        break;
      case GdoEventGroup.ALARM2:
        setEvent(Config.EVENT.EVENT_NAME.DISABLED[langCode], event.garagePercentage === 0
          ? "../../../assets/images/gdo/event/Disable-Close.svg" : event.garagePercentage === 100
            ? "../../../assets/images/gdo/event/Disable-Open.svg" : "../../../assets/images/gdo/event/Disable-Open-Partial.svg", Config.EVENT.COLOR.Gray, event.eventDes);
        break;
      case GdoEventGroup.ALARM:
        setEvent(Config.EVENT.EVENT_NAME.OBSTRUCTION_AT[langCode], event.garagePercentage === 0
          ? "../../../assets/images/gdo/event/Red-Open.svg" : event.garagePercentage === 100
            ? "../../../assets/images/gdo/event/Red-Closed.svg" : "../../../assets/images/gdo/event/Red-Open-Partial.svg", Config.EVENT.COLOR.Red, Config.EVENT.EVENT_BY.OBSTRUCTION_FAILURE[langCode]);
        break;
      case GdoEventGroup.SENSOR:
        setEvent(Config.EVENT.EVENT_NAME.OBSTRUCTION_AT[langCode], "../../../assets/images/gdo/event/Red-Open-Partial.svg", Config.EVENT.COLOR.Red, Config.EVENT.EVENT_BY.SENSOR_OBSTRUCTION[langCode]);
        break;
      default:
        this.handleDefaultEvent(event, langCode, setEvent);
    }

  }

  private handleGdoOperationEvent(event: IGDOHistory, langCode: string, setEvent: Function) {
    if (event.eventBy != GdoEventBy.LCU_OBSTACLE && event.eventBy != GdoEventBy.LCU_ALIGNED_SENSOR) {
      switch (event.operationEvent) {
        case EventGdoStatus.CLOSED:
          setEvent(Config.EVENT.EVENT_NAME.CLOSE[langCode], "../../../assets/images/gdo/event/Closed.svg", Config.EVENT.COLOR.Orange, event.eventDes, event.garagePercentage);
          break;
        case EventGdoStatus.INTERIM_POS:
        case EventGdoStatus.PARTIAL:
          setEvent(Config.EVENT.EVENT_NAME.OPEN[langCode], "../../../assets/images/gdo/event/Open-Partial.svg", Config.EVENT.COLOR.Green, event.eventDes, event.garagePercentage);
          break;
        case EventGdoStatus.OPEN:
          setEvent(Config.EVENT.EVENT_NAME.OPEN[langCode], "../../../assets/images/gdo/event/Open.svg", Config.EVENT.COLOR.Green, event.eventDes, event.garagePercentage);
          break;
        case EventGdoStatus.AUTO:
          this.handleAutoGdoOperationEvent(event, langCode, setEvent);
          break;
      }
    } else {
      this.handleDefaultEvent(event, langCode, setEvent);
    }
  }

  private handleAutoGdoOperationEvent(event: IGDOHistory, langCode: string, setEvent: Function) {
    switch (event.garageState) {
      case EventAutoGdoStatus.CLOSED:
        setEvent(Config.EVENT.EVENT_NAME.CLOSE[langCode], "../../../assets/images/gdo/event/Closed.svg", Config.EVENT.COLOR.Orange, event.eventDes, event.garagePercentage);
        break;
      case EventAutoGdoStatus.INTERIM_POS:
      case EventAutoGdoStatus.PARTIAL:
      case EventAutoGdoStatus.OPENING:
      case EventAutoGdoStatus.CLOSING:
        setEvent(Config.EVENT.EVENT_NAME.OPEN[langCode], "../../../assets/images/gdo/event/Open-Partial.svg", Config.EVENT.COLOR.Green, event.eventDes, event.garagePercentage);
        break;
      case EventAutoGdoStatus.OPEN:
        setEvent(Config.EVENT.EVENT_NAME.OPEN[langCode], "../../../assets/images/gdo/event/Open.svg", Config.EVENT.COLOR.Green, event.eventDes, event.garagePercentage);
        break;
    }
  }


  private handleWifiManagementEvent(event: IGDOHistory, langCode: string, setEvent: Function) {
    switch (event.operationEvent) {
      case GdoWifiStatus.DISCONNECTED:
        setEvent(Config.EVENT.EVENT_NAME.WI_FI_DISCONNECTED[langCode], "../../../assets/images/gdo/event/Wifi-Disconnect-Event.svg", Config.EVENT.COLOR.Red, '');
        break;
      case GdoWifiStatus.CONNECTED:
        setEvent(Config.EVENT.EVENT_NAME.WI_FI_CONNECTED[langCode], "../../../assets/images/gdo/event/Wifi-Connect-Event.svg", Config.EVENT.COLOR.Blue, event.ssidWifi);
        break;
    }
  }

  private handleVacationModeEvent(event: IGDOHistory, langCode: string, setEvent: Function) {
    switch (event.operationEvent) {
      case GdoVacationMode.OFF:
        setEvent(Config.EVENT.EVENT_NAME.VACATION_OFF[langCode], "../../../assets/images/gdo/event/Secure.svg", Config.EVENT.COLOR.Gray, event.eventDes);
        break;
      case GdoVacationMode.ON:
        setEvent(Config.EVENT.EVENT_NAME.VACATION_ON[langCode], "../../../assets/images/gdo/event/Secure.svg", Config.EVENT.COLOR.Gray, event.eventDes);
        break;
    }
  }

  private handleBatteryEvent(event: IGDOHistory, langCode: string, setEvent: Function) {
    switch (event.operationEvent) {
      case GdoBatteryStatus.BATTERY_FLAT:
        setEvent(Config.EVENT.EVENT_NAME.BATTERY_FLAT[langCode], "../../../assets/images/gdo/event/Battery_Flat_Event.svg", Config.EVENT.COLOR.Red, '');
        break;
      case GdoBatteryStatus.BATTERY_LOW:
        setEvent(Config.EVENT.EVENT_NAME.BATTERY_LOW[langCode], "../../../assets/images/gdo/event/Battery_Low_Event.svg", Config.EVENT.COLOR.Red, '');
        break;
      case GdoBatteryStatus.POWER_PLUGIN:
        setEvent(Config.EVENT.EVENT_NAME.AC_POWER[langCode], "../../../assets/images/gdo/event/Plug.svg", Config.EVENT.COLOR.Blue, '');
        break;
      default:
        setEvent(Config.EVENT.EVENT_NAME.ON_BATTERY[langCode], "../../../assets/images/gdo/event/Battery_Full_Blue-Event.svg", Config.EVENT.COLOR.Blue, '');
    }
  }

  private handleDefaultEvent(event: IGDOHistory, langCode: string, setEvent: Function) {
    if (event.eventBy === GdoEventBy.LCU_ALIGNED_SENSOR) {
      setEvent(Config.EVENT.EVENT_NAME.OBSTRUCTION[langCode], "../../../assets/images/gdo/event/Red-Open-Partial.svg", Config.EVENT.COLOR.Red, Config.EVENT.EVENT_BY.SENSOR_OBSTRUCTION[langCode]);
    } else if (event.eventBy === GdoEventBy.LCU_OBSTACLE) {
      setEvent(Config.EVENT.EVENT_NAME.OPEN[langCode], "../../../assets/images/gdo/event/Red-Closed.svg", Config.EVENT.COLOR.Red, Config.EVENT.EVENT_BY.OBSTRUCTION_FAILURE[langCode]);
    }
  }

  private setEventName(langCode: string, eventName: string, fromHomePage: boolean, event: IGDOHistory, percentage?: number) {
    let openerName = fromHomePage ? `${Config.EVENT.EVENT_NAME.OPENER[langCode]} ${event.deviceName}` : `${Config.EVENT.EVENT_NAME.OPENER[langCode]}`;
    if (event && (event.eventGroupId === GdoEventGroup.SENSOR || event.eventGroupId === GdoEventGroup.ALARM)) {
      return `${openerName}: ${eventName} ${event.garagePercentage}%`;
    }
    if (percentage && percentage > 0 && percentage < 100) {
      return `${openerName}: ${percentage}% ${eventName}`;
    }
    return `${openerName}: ${eventName}`;
  }

  private getEventBy(event: IGDOHistory, langCode: string) {
    const eventBy = Number.parseInt(event.eventBy);
    if (eventBy === GdoEventBy.BLE_SC || eventBy === GdoEventBy.WIFI) {
      event.eventDes = event.user;
    } else if (eventBy === GdoEventBy.AUTO_CLOSE_TIME) {
      event.eventDes = Config.EVENT.EVENT_BY.AUTO_CLOSE_SCHEDULE[langCode];
    } else if (eventBy === GdoEventBy.AUTO_CLOSE_DELAY) {
      event.eventDes = Config.EVENT.EVENT_BY.AUTO_CLOSE_DELAY[langCode];
    } else if (eventBy === GdoEventBy.REMOTE) {
      event.eventDes = Config.EVENT.EVENT_BY.REMOTE[langCode];
    } else if (eventBy === GdoEventBy.WALL_STATION) {
      event.eventDes = Config.EVENT.EVENT_BY.WS[langCode];
    } else if (eventBy === GdoEventBy.MATTER) {
      event.eventDes = Config.EVENT.EVENT_BY.MATTER[langCode];
    } else if (eventBy === GdoEventBy.KEYPAD) {
      event.eventDes = Config.EVENT.EVENT_BY.MATTER[langCode];
    }

    // Smart Home
    if (event.user?.includes('@Alexa@')) {
      event.eventDes = `${Config.EVENT.EVENT_BY.ALEXA[langCode]} / ${event.user.replace('@Alexa@', '')}`;
    } else if (event.user?.includes('@GoogleHome@')) {
      event.eventDes = `${Config.EVENT.EVENT_BY.GOOGLE[langCode]} / ${event.user.replace('@GoogleHome@', '')}`;
    } else if (event.user?.includes('@SmartThings@')) {
      event.eventDes = `${Config.EVENT.EVENT_BY.SMART_THINGS[langCode]} / ${event.user.replace('@SmartThings@', '')}`;
    }
  }

  toMidnightTimestamp(timestamp: number): number {
    const date = new Date(timestamp);
    date.setHours(0, 0, 0, 0);
    return Math.floor(date.getTime() / 1000);
  }

  toEndDayTimestamp(timestamp: number): number {
    const date = new Date(timestamp);
    date.setHours(23, 59, 59, 999);
    return Math.floor(date.getTime() / 1000);
  }
}
